import type { FC } from 'react';
import { useEffect, useState } from 'react';
import Button from '@/ui/components/Button/Button';
import { useGA4 } from '@/ui/context/ga4';
import cn from '@/ui/utils/cn/cn';

import $ from './Header.module.scss';

export interface NavigationItem {
  title: string;
  sectionId: string;
}

interface HeaderSummaryNavigationProps {
  items: NavigationItem[];
}

const HeaderSummaryNavigation: FC<HeaderSummaryNavigationProps> = ({
  items,
}) => {
  const { trackMenuNavigation } = useGA4();
  const [activeSection, setActiveSection] = useState<string>(
    items.length > 0 ? items[0].sectionId : '',
  );

  const scrollToSection = (sectionId: string, title: string) => {
    // Immediately set the active section for instant visual feedback
    setActiveSection(sectionId);

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      trackMenuNavigation({
        menu: 'header',
        menu_path: `#${sectionId}`,
        menu_text: title,
        menu_click: 'text',
      });
    }
  };

  useEffect(() => {
    if (items.length === 0) {
      return;
    }

    const observerOptions = {
      rootMargin: '-20% 0px -60% 0px', // Trigger when section is 20% from top
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    // Observe all sections
    items.forEach(item => {
      const element = document.getElementById(item.sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    // Handle case when user is at the top of the page
    const handleScroll = () => {
      if (window.scrollY < 100) {
        setActiveSection(items[0].sectionId);
      }
    };

    window.addEventListener('scroll', handleScroll);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [items]);

  return (
    <ul className={$.list}>
      {items.map(item => (
        <li
          key={item.sectionId}
          className={cn(
            $.listItem,
            $.desktop,
            activeSection === item.sectionId && $.active,
          )}
        >
          <Button
            variant="tertiary"
            className={$.listItemButton}
            onClick={() => scrollToSection(item.sectionId, item.title)}
          >
            {item.title}
          </Button>
        </li>
      ))}
    </ul>
  );
};

export default HeaderSummaryNavigation;
